/** Echo form-entry modalities (Add Study: form + file in one view). */
export const ECHO_FORM_MODALITY_KEYS = ["pre_echo", "post_echo"];

export const isEchoFormModality = (key) => ECHO_FORM_MODALITY_KEYS.includes(key);

export const PVL_SEVERITY_OPTIONS = ["Mild", "Moderate", "Severe"];
export const YES_NO_OPTIONS = ["Yes", "No"];

/**
 * @typedef {Object} IEchoMeasurementField
 * @property {string} name
 * @property {'number' | 'yesno' | 'pvl'} type
 * @property {string} [unit]
 */

/** Field names must match POST /api/studies measurements[].name */
export const PRE_ECHO_MEASUREMENT_FIELDS = [
  { name: "Peak Velocity", type: "number", unit: "m/s" },
  { name: "Mean Pressure Gradient", type: "number", unit: "mmHg" },
  { name: "Aortic Valve Area (AVA)", type: "number", unit: "cm²" },
  { name: "Dimensionless Velocity Index (DVI)", type: "number" },
  { name: "Indexed Aortic Valve Area I", type: "number", unit: "cm²/m²" },
  { name: "PVL", type: "pvl" },
  { name: "Existing Pacemaker", type: "yesno" },
];

export const POST_ECHO_MEASUREMENT_FIELDS = [
  { name: "Paravalvular Leak (PVL)", type: "pvl" },
  { name: "Mean Pressure Gradient", type: "number", unit: "mmHg" },
  { name: "Peak Gradient", type: "number", unit: "mmHg" },
  { name: "New Pacemaker", type: "yesno" },
];

export const getEchoMeasurementFields = (modality) => {
  if (modality === "pre_echo") return PRE_ECHO_MEASUREMENT_FIELDS;
  if (modality === "post_echo") return POST_ECHO_MEASUREMENT_FIELDS;
  return [];
};

export const emptyEchoMeasurementValues = (modality) => {
  const values = {};
  getEchoMeasurementFields(modality).forEach((field) => {
    values[field.name] = "";
    if (field.type === "pvl") values[`${field.name}__present`] = "";
  });
  return values;
};

export const pvlDisplayValue = (present, severity) => {
  if (present === "No") return "No";
  if (present === "Yes" && severity) return severity;
  return "";
};

export const buildEchoMeasurements = (modality, values, { partial = false } = {}) => {
  const missing = [];
  const measurements = [];

  getEchoMeasurementFields(modality).forEach((field) => {
    if (field.type === "pvl") {
      const present = String(values?.[`${field.name}__present`] ?? "").trim();
      const severity = String(values?.[field.name] ?? "").trim();
      if (!present) {
        if (!partial) missing.push(field.name);
        return;
      }
      if (present === "Yes" && !severity) {
        if (!partial) missing.push(`${field.name} severity`);
        return;
      }
      measurements.push({
        name: field.name,
        value: pvlDisplayValue(present, severity),
      });
      return;
    }

    const value = String(values?.[field.name] ?? "").trim();
    if (!value) {
      if (!partial) missing.push(field.name);
      return;
    }
    measurements.push({ name: field.name, value });
  });

  return { measurements, missing };
};

export const isEchoMeasurementValuesEmpty = (modality, values) => (
  getEchoMeasurementFields(modality).every((field) => {
    const value = String(values?.[field.name] ?? "").trim();
    if (field.type === "pvl") {
      const present = String(values?.[`${field.name}__present`] ?? "").trim();
      return !present && !value;
    }
    return !value;
  })
);

export const isFormOnlyStudy = (study) => {
  if (study?.file_available === false || study?.fileAvailable === false) return true;
  const measurements = study?.measurements;
  if (!Array.isArray(measurements) || measurements.length === 0) return false;
  return (study?.files_count ?? 0) === 0;
};
